import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { HeroSearch } from "@/components/HeroSearch";
import { topicMeta } from "@/lib/topicMeta";
import { Clock, ChevronRight, Loader2, SearchX } from "lucide-react";

function highlight(text, q) {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"));
  return parts.map((p, i) =>
    p.toLowerCase() === q.toLowerCase() ? (
      <mark key={i} className="bg-blue-100 text-[#174EA6] rounded px-0.5">{p}</mark>
    ) : (
      <span key={i}>{p}</span>
    )
  );
}

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    api
      .get("/search", { params: { q } })
      .then((r) => setResults(r.data))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <main data-testid="search-page" className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 mb-6 text-center">
        Search the Help Center
      </h1>
      <HeroSearch autoFocus defaultValue={q} />

      <div className="mt-12">
        {loading ? (
          <div className="py-16 grid place-items-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        ) : q.trim() === "" ? (
          <p className="text-center text-slate-400">Type a question above to search all articles.</p>
        ) : results.length === 0 ? (
          <div className="text-center py-16" data-testid="search-empty-state">
            <SearchX className="w-12 h-12 mx-auto text-slate-300" />
            <p className="mt-4 text-slate-600 font-medium">No results for “{q}”</p>
            <p className="text-sm text-slate-400 mt-1">Try different keywords or browse topics from the home page.</p>
            <Link to="/" className="text-[#1A73E8] hover:underline mt-3 inline-block text-sm font-medium">Browse topics</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mb-4" data-testid="search-result-count">
              {results.length} result{results.length !== 1 ? "s" : ""} for “{q}”
            </p>
            <div className="space-y-3">
              {results.map((a) => {
                const { Icon, color, bg } = topicMeta(a.topic_icon);
                return (
                  <Link
                    key={a.id}
                    to={`/article/${a.slug}`}
                    data-testid="search-result-item"
                    className="group flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 hover:shadow-md transition-all"
                  >
                    <span className="grid place-items-center w-10 h-10 rounded-lg shrink-0" style={{ backgroundColor: bg, color }}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-semibold text-slate-400">
                        {a.topic_name} <span className="text-slate-300">›</span> {a.section}
                      </div>
                      <h4 className="font-medium text-slate-900 group-hover:text-[#1A73E8] transition-colors">
                        {highlight(a.title, q)}
                      </h4>
                      <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{highlight(a.excerpt, q)}</p>
                      <span className="inline-flex items-center gap-1 mt-2 text-xs text-slate-400">
                        <Clock className="w-3.5 h-3.5" /> {a.read_time} min read
                      </span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-slate-500 self-center shrink-0" />
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
